import {
  CurrencyDollarIcon,
  UsersIcon,
  ArrowTrendingDownIcon,
  UserGroupIcon,
  BoltIcon,
  FireIcon,
  UserPlusIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { PageHeader, Card } from '@/components/ui/Card';
import { formatCurrency, formatNumber, formatPercent, humanizeEvent } from '@/lib/format';
import { useAdminDashboard } from './useAdminDashboard';
import { useAdminAnalytics } from './useAdminAnalytics';
import { KpiStatCard } from './blocks/KpiStatCard';
import { ChartCard } from './blocks/ChartCard';
import RevenueTrendChart from './blocks/RevenueTrendChart';
import RankedBars from './blocks/RankedBars';
import NewVsReturningChart from './blocks/NewVsReturningChart';
import ActivationFunnel from './blocks/ActivationFunnel';

const DashboardPage = () => {
  const { overview, overviewLoading, revenueTrend, revenueTrendLoading, revenueTrendError } =
    useAdminDashboard();
  const {
    posthogConfigured,
    activeUsers,
    registeredUsers,
    topEvents,
    featureAdoption,
    newVsReturning,
    activationFunnel,
    cohorts,
  } = useAdminAnalytics();

  const au = activeUsers.data;
  const registered = registeredUsers.data;
  const activePct =
    au && registered && registered.total > 0
      ? Math.round((au.mau / registered.total) * 1000) / 10
      : undefined;

  const lastSynced = overview?.lastSyncedAt
    ? new Date(overview.lastSyncedAt).toLocaleString('fr-FR')
    : undefined;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={lastSynced ? `Données Stripe synchronisées le ${lastSynced}` : 'Espace d\'administration OpenTri'}
      />

      {!posthogConfigured && (
        <Card className="mb-4 flex items-center gap-3 border-warning-500/30 bg-warning-500/5">
          <InformationCircleIcon className="h-5 w-5 shrink-0 text-warning-500" />
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Les indicateurs d'engagement PostHog ne sont pas configurés (clé API projet manquante).
            Les blocs Stripe et inscriptions restent disponibles.
          </p>
        </Card>
      )}

      {/* Row 1 — Executive KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiStatCard
          label="MRR"
          value={overview ? formatCurrency(overview.mrr) : '—'}
          hint={overview ? `ARR ${formatCurrency(overview.arr)}` : undefined}
          icon={<CurrencyDollarIcon className="h-5 w-5" />}
          isLoading={overviewLoading}
        />
        <KpiStatCard
          label="Abonnements actifs"
          value={overview ? formatNumber(overview.activeSubscribers) : '—'}
          icon={<UsersIcon className="h-5 w-5" />}
          isLoading={overviewLoading}
        />
        <KpiStatCard
          label="Taux de churn (mois)"
          value={overview ? formatPercent(overview.churnRate) : '—'}
          icon={<ArrowTrendingDownIcon className="h-5 w-5" />}
          isLoading={overviewLoading}
        />
        <KpiStatCard
          label="ARPU"
          value={overview ? formatCurrency(overview.arpu, true) : '—'}
          icon={<UserGroupIcon className="h-5 w-5" />}
          isLoading={overviewLoading}
        />
      </div>

      {/* Row 2 — Audience */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiStatCard
          label="Actifs (MAU / WAU / DAU)"
          value={au ? `${formatNumber(au.mau)} / ${formatNumber(au.wau)} / ${formatNumber(au.dau)}` : '—'}
          icon={<BoltIcon className="h-5 w-5" />}
          isLoading={activeUsers.isLoading}
        />
        <KpiStatCard
          label="Stickiness (DAU/MAU)"
          value={au ? formatPercent(au.stickiness) : '—'}
          icon={<FireIcon className="h-5 w-5" />}
          isLoading={activeUsers.isLoading}
        />
        <KpiStatCard
          label="Actifs vs inscrits"
          value={au && registered ? `${formatNumber(au.mau)} / ${formatNumber(registered.total)}` : '—'}
          hint={activePct !== undefined ? `${formatPercent(activePct)} des inscrits actifs` : undefined}
          icon={<UsersIcon className="h-5 w-5" />}
          isLoading={activeUsers.isLoading || registeredUsers.isLoading}
        />
        <KpiStatCard
          label="Inscrits (total)"
          value={registered ? formatNumber(registered.total) : '—'}
          icon={<UserPlusIcon className="h-5 w-5" />}
          isLoading={registeredUsers.isLoading}
        />
      </div>

      {/* Revenue + engagement charts */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard
          title="Évolution du MRR"
          subtitle="12 derniers mois"
          isLoading={revenueTrendLoading}
          isError={revenueTrendError}
          isEmpty={revenueTrend.length === 0}
        >
          <RevenueTrendChart data={revenueTrend} />
        </ChartCard>

        <ChartCard
          title="Nouveaux vs récurrents"
          subtitle="30 derniers jours"
          isLoading={newVsReturning.isLoading}
          isError={posthogConfigured && newVsReturning.isError}
          isEmpty={!posthogConfigured || (newVsReturning.data?.length ?? 0) === 0}
          emptyLabel={posthogConfigured ? 'Aucune donnée' : 'PostHog non configuré'}
        >
          <NewVsReturningChart data={newVsReturning.data ?? []} />
        </ChartCard>
      </div>

      {/* Row 3 — Product */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard
          title="Adoption des fonctionnalités"
          subtitle="% des actifs (MAU), 30 j"
          isLoading={featureAdoption.isLoading}
          isError={posthogConfigured && featureAdoption.isError}
          isEmpty={!posthogConfigured || (featureAdoption.data?.length ?? 0) === 0}
          emptyLabel={posthogConfigured ? 'Aucune donnée' : 'PostHog non configuré'}
        >
          <RankedBars
            items={(featureAdoption.data ?? []).map((f) => ({
              label: humanizeEvent(f.event),
              value: f.pct,
              valueLabel: `${formatPercent(f.pct)} · ${formatNumber(f.users)}`,
            }))}
          />
        </ChartCard>

        <ChartCard
          title="Événements les plus fréquents"
          subtitle="30 derniers jours"
          isLoading={topEvents.isLoading}
          isError={posthogConfigured && topEvents.isError}
          isEmpty={!posthogConfigured || (topEvents.data?.length ?? 0) === 0}
          emptyLabel={posthogConfigured ? 'Aucune donnée' : 'PostHog non configuré'}
        >
          <RankedBars
            items={(topEvents.data ?? []).map((e) => ({
              label: humanizeEvent(e.event),
              value: e.total,
              valueLabel: formatNumber(e.total),
            }))}
          />
        </ChartCard>

        <ChartCard
          title="Cohortes les plus actives"
          subtitle="Par statut d'abonnement, 30 j"
          isLoading={cohorts.isLoading}
          isError={posthogConfigured && cohorts.isError}
          isEmpty={!posthogConfigured || (cohorts.data?.length ?? 0) === 0}
          emptyLabel={posthogConfigured ? 'Aucune donnée' : 'PostHog non configuré'}
        >
          <RankedBars
            items={(cohorts.data ?? []).map((c) => ({
              label: humanizeEvent(c.segment),
              value: c.users,
              valueLabel: formatNumber(c.users),
            }))}
          />
        </ChartCard>
      </div>

      {/* Activation funnel */}
      <div className="mt-4 grid grid-cols-1 gap-4">
        <ChartCard
          title="Funnel d'activation"
          subtitle="Onboarding → abonnement, 90 j"
          isLoading={activationFunnel.isLoading}
          isError={posthogConfigured && activationFunnel.isError}
          isEmpty={!posthogConfigured || (activationFunnel.data?.length ?? 0) === 0}
          emptyLabel={posthogConfigured ? 'Aucune donnée' : 'PostHog non configuré'}
        >
          <ActivationFunnel steps={activationFunnel.data ?? []} />
        </ChartCard>
      </div>
    </>
  );
};

export default DashboardPage;
